import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  DollarOutlined,
  GlobalOutlined,
  ExportOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import {
  fetchUsers,
  updateUser,
  updateUserStatus,
  resetUserPassword,
  resetUserGa,
  createUser,
  deleteUser,
  batchDeleteUsers,
  adjustUserBalance,
  fetchUserLevels,
  setFilters,
  setPagination,
  clearError,
  merchantRecharge,
  merchantWithdraw,
} from '../../features/user/userSlice';
import { formatDateTime, formatAmount, getStatusColor, getStatusText } from '../../utils/index';
import { get } from '../../utils/api';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusTone = (status) => {
  const color = getStatusColor(status);
  if (color === 'success') return 'paid';
  if (color === 'error') return 'failed';
  if (color === 'warning') return 'processing';
  if (color === 'processing') return 'pending';
  return 'muted';
};

const UserList = () => {
  const dispatch = useDispatch();
  const { users, pagination, filters, isLoading, error, userLevels } = useSelector((state) => state.user);
  
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [userDetailVisible, setUserDetailVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetGaVisible, setResetGaVisible] = useState(false);
  const [createUserVisible, setCreateUserVisible] = useState(false);
  const [createUserForm] = Form.useForm();
  const [batchDeleteVisible, setBatchDeleteVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [editUserForm] = Form.useForm();
  const [userToEdit, setUserToEdit] = useState(null);
  const [balanceAdjustConfirmVisible, setBalanceAdjustConfirmVisible] = useState(false);
  const [balanceAdjustForm] = Form.useForm();
  const [originalBalance, setOriginalBalance] = useState(0);
  const [newBalance, setNewBalance] = useState(0);
  const [regions, setRegions] = useState([]);
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [withdrawVisible, setWithdrawVisible] = useState(false);
  const [amountForm] = Form.useForm();

  // 使用JSON.stringify来避免对象引用变化导致的无限循环
  const filtersString = JSON.stringify(filters);
  const paginationString = JSON.stringify(pagination);

  useEffect(() => {
    dispatch(fetchUsers({ ...filters, ...pagination }));
  }, [dispatch, filtersString, paginationString]);

  useEffect(() => {
    // 获取用户等级列表
    dispatch(fetchUserLevels());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '操作失败');
      message.error(errorMessage);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 获取地区列表
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await get('/admin/regions/all');
        if (res.code === 200) {
          setRegions(res.data || []);
        }
      } catch (error) {
        console.error('获取地区列表失败:', error);
      }
    };
    fetchRegions();
  }, []);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '用户ID',
      dataIndex: 'id',
      key: 'id',
      width: 88,
      render: (val) => val ? <span className="tp-mono">{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (val) => val ? <span className="tp-mono">{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '姓名',
      dataIndex: 'real_name',
      key: 'real_name',
      width: 120,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '地区',
      dataIndex: 'region_code',
      key: 'region_code',
      width: 120,
      render: (regionCode) => {
        if (!regionCode) return <span className="tp-empty">-</span>;
        const region = regions.find(r => r.regionCode === regionCode || r.region_code === regionCode);
        return region ? (
          <Tooltip title={`${region.regionName || region.region_name}${region.regionNameEn ? ` (${region.regionNameEn})` : ''}`}>
            <span className="tp-code">{regionCode}</span>
          </Tooltip>
        ) : (
          <span className="tp-code">{regionCode}</span>
        );
      },
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 130,
      render: (balance) => <span className="tp-num">{formatAmount(balance)}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 88,
      render: (status) => (
        <span className={`tp-tag tp-tag--${statusTone(status)}`}>
          {getStatusText(status)}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date) => date ? <span className="tp-time">{formatDateTime(date)}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_time',
      key: 'last_login_time',
      width: 160,
      render: (date) => date ? <span className="tp-time">{formatDateTime(date)}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 196,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-icon-actions">
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="重置密码">
            <Button
              type="text"
              icon={<LockOutlined />}
              onClick={() => handleResetPassword(record)}
            />
          </Tooltip>
          <Tooltip title="重置Google验证器">
            <Button
              type="text"
              icon={<SafetyCertificateOutlined />}
              onClick={() => handleResetGa(record)}
            />
          </Tooltip>
          <Tooltip title="充值">
            <Button
              type="text"
              icon={<ExportOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setRechargeVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="提现">
            <Button
              type="text"
              icon={<ImportOutlined />}
              onClick={() => {
                setSelectedUser(record);
                setWithdrawVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="删除用户">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteUser(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (user) => {
    setSelectedUser(user);
    setUserDetailVisible(true);
  };

  // 编辑用户
  const handleEdit = (user) => {
    setUserToEdit(user);
    setEditUserVisible(true);
    setOriginalBalance(parseFloat(user.balance) || 0);
    
    // 回填表单数据
    editUserForm.setFieldsValue({
      realName: user.real_name,
      idCard: user.id_card,
      email: user.email,
      userLevel: user.user_level,
      status: user.status,
      balance: parseFloat(user.balance) || 0, // 转换为数字类型
      regionCode: user.region_code,
      payPassword: '', // 交易密码不预填，需要重新输入
    });
  };

  // 重置密码
  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordVisible(true);
  };

  // 重置Google验证器
  const handleResetGa = (user) => {
    setSelectedUser(user);
    setResetGaVisible(true);
  };

  // 确认重置Google验证器
  const handleConfirmResetGa = async () => {
    if (!selectedUser) return;
    try {
      await dispatch(resetUserGa({ userId: selectedUser.id })).unwrap();
      message.success('Google验证器已重置，用户可重新绑定');
      setResetGaVisible(false);
      setSelectedUser(null);
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '重置Google验证器失败');
      message.error(errorMessage);
    }
  };

  // 确认重置密码
  const handleConfirmResetPassword = async (values) => {
    try {
      await dispatch(resetUserPassword({
        userId: selectedUser.id,
        newPassword: values.newPassword,
      })).unwrap();
      message.success('密码重置成功');
      setResetPasswordVisible(false);
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '重置密码失败');
      message.error(errorMessage);
    }
  };

  // 创建用户
  const handleCreateUser = () => {
    setCreateUserVisible(true);
    createUserForm.resetFields();
  };

  // 确认创建用户
  const handleConfirmCreateUser = async (values) => {
    try {
      await dispatch(createUser({
        phone: values.phone,
        password: values.password,
        realName: values.realName,
        idCard: values.idCard,
        email: values.email,
        userLevel: values.userLevel || 1,
        initialBalance: values.initialBalance || 0,
        invitedByCode: values.invitedByCode,
        regionCode: values.regionCode,
      })).unwrap();
      message.success('用户创建成功');
      setCreateUserVisible(false);
      createUserForm.resetFields();
      // 刷新用户列表
      dispatch(fetchUsers({ ...filters, ...pagination }));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '创建用户失败');
      message.error(errorMessage);
    }
  };

  // 批量删除用户
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的用户');
      return;
    }
    setBatchDeleteVisible(true);
  };

  // 确认批量删除
  const handleConfirmBatchDelete = async () => {
    try {
      const result = await dispatch(batchDeleteUsers(selectedRowKeys)).unwrap();
      
      // 显示删除结果
      const { summary, results } = result;
      if (summary.success > 0) {
        message.success(`成功删除 ${summary.success} 个用户`);
      }
      
      if (summary.failed > 0) {
        // 显示失败的用户信息
        const failedUsers = results.failed.map(item => 
          `用户ID ${item.userId}: ${item.reason}`
        ).join('\n');
        message.warning(`有 ${summary.failed} 个用户删除失败:\n${failedUsers}`);
      }
      
      setBatchDeleteVisible(false);
      setSelectedRowKeys([]);
      // 刷新用户列表
      dispatch(fetchUsers({ ...filters, ...pagination }));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '批量删除用户失败');
      message.error(errorMessage);
    }
  };

  // 单个删除用户
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteConfirmVisible(true);
  };

  // 确认单个删除
  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      message.success('用户删除成功');
      setDeleteConfirmVisible(false);
      setUserToDelete(null);
      // 刷新用户列表
      dispatch(fetchUsers({ ...filters, ...pagination }));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '删除用户失败');
      message.error(errorMessage);
    }
  };

  // 确认编辑用户
  const handleConfirmEditUser = async (values) => {
    try {
      const balanceChanged = parseFloat(values.balance) !== originalBalance;
      
      // 如果余额发生变化，需要二次确认
      if (balanceChanged) {
        const balanceDiff = parseFloat(values.balance) - originalBalance;
        setNewBalance(parseFloat(values.balance));
        balanceAdjustForm.resetFields();
        setBalanceAdjustConfirmVisible(true);
        return; // 先不提交，等待二次确认
      }

      // 余额未变化，直接更新用户信息
      const updateData = {
        realName: values.realName,
        idCard: values.idCard,
        email: values.email,
        userLevel: values.userLevel,
        status: values.status,
        regionCode: values.regionCode,
      };
      
      // 如果填写了交易密码，则添加到更新数据中
      if (values.payPassword && values.payPassword.trim() !== '') {
        updateData.payPassword = values.payPassword;
      }
      
      await dispatch(updateUser({
        userId: userToEdit.id,
        data: updateData
      })).unwrap();
      message.success('用户信息更新成功');
      setEditUserVisible(false);
      setUserToEdit(null);
      editUserForm.resetFields();
      // 刷新用户列表
      dispatch(fetchUsers({ ...filters, ...pagination }));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '更新用户信息失败');
      message.error(errorMessage);
    }
  };

  // 确认余额调整
  const handleConfirmBalanceAdjust = async (values) => {
    try {
      const balanceDiff = newBalance - originalBalance;
      const adjustmentType = balanceDiff > 0 ? 'increase' : 'decrease';
      const adjustAmount = Math.abs(balanceDiff);

      // 先调整余额（后台管理员不需要支付密码）
      await dispatch(adjustUserBalance({
        userId: userToEdit.id,
        adjustmentType,
        amount: adjustAmount,
        reason: values.reason,
        payPassword: '', // 后台管理员不需要支付密码
      })).unwrap();

      // 再更新其他用户信息
      const formValues = editUserForm.getFieldsValue();
      const updateData = {
        realName: formValues.realName,
        idCard: formValues.idCard,
        email: formValues.email,
        userLevel: formValues.userLevel,
        status: formValues.status,
        regionCode: formValues.regionCode,
      };
      
      // 如果填写了交易密码，则添加到更新数据中
      if (formValues.payPassword && formValues.payPassword.trim() !== '') {
        updateData.payPassword = formValues.payPassword;
      }
      
      await dispatch(updateUser({
        userId: userToEdit.id,
        data: updateData
      })).unwrap();

      message.success('用户信息及余额更新成功');
      setBalanceAdjustConfirmVisible(false);
      setEditUserVisible(false);
      setUserToEdit(null);
      editUserForm.resetFields();
      balanceAdjustForm.resetFields();
      // 刷新用户列表
      dispatch(fetchUsers({ ...filters, ...pagination }));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error?.message || '余额调整失败');
      message.error(errorMessage);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar">
        <Form form={form} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="keyword">
            <Input
              placeholder="搜索手机号/姓名"
              prefix={<SearchOutlined />}
              className="tp-field--lg"
              allowClear
            />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="用户状态" className="tp-field--sm" allowClear>
              <Option value="1">正常</Option>
              <Option value="2">冻结</Option>
              <Option value="0">禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item name="level">
            <Select placeholder="用户等级" className="tp-field--sm" allowClear>
              {userLevels
                .filter(level => level.status === 1)
                .map(level => (
                  <Option key={level.id} value={level.level}>
                    Lv.{level.level}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker className="tp-field--date" placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateUser}>新增用户</Button>
            <Button
              danger
              disabled={selectedRowKeys.length === 0}
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
            >
              批量删除
            </Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-table-card">
        <div className="tp-table-head">
          <div className="tp-table-head__count">共 {pagination.total} 条记录</div>
        </div>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading}
          size="middle"
          rowSelection={rowSelection}
          scroll={{ x: 1280 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* 用户详情抽屉 */}
      <Drawer
        title="用户详情"
        width={600}
        open={userDetailVisible}
        onClose={() => setUserDetailVisible(false)}
      >
        {selectedUser && (
          <div>
            {/* 资产统计 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="用户余额"
                  value={selectedUser.balance}
                  prefix={<DollarOutlined />}
                  formatter={(value) => formatAmount(value)}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="金豆"
                  value={selectedUser.gold_beans || 0}
                  prefix="💰"
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="用户等级"
                  value={selectedUser.user_level}
                  prefix={<UserOutlined />}
                  suffix="级"
                />
              </Col>
            </Row>

            {/* 累计统计 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="累计充值"
                  value={selectedUser.total_recharge || 0}
                  prefix="¥"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="累计提现"
                  value={selectedUser.total_withdraw || 0}
                  prefix="¥"
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="邀请人数"
                  value={selectedUser.invite_count || 0}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>

            {/* 基本信息 */}
            <Descriptions
              title="基本信息"
              bordered
              column={1}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="用户ID">
                {selectedUser.id}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                <Space>
                  <PhoneOutlined />
                  {selectedUser.phone}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">
                <Space>
                  <MailOutlined />
                  {selectedUser.email || '-'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="姓名">
                {selectedUser.real_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="身份证号">
                {selectedUser.id_card || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="邀请码">
                <Tag color="blue">{selectedUser.invite_code || '-'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="邀请人">
                {selectedUser.invited_by_code ? (
                  <Tag color="green">{selectedUser.invited_by_code}</Tag>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="所属地区">
                {selectedUser.region_code ? (
                  <Space direction="vertical" size={4}>
                    <Space>
                      <Tag icon={<GlobalOutlined />} color="cyan">
                        {selectedUser.region_code}
                      </Tag>
                      {(() => {
                        const region = regions.find(r => r.regionCode === selectedUser.region_code || r.region_code === selectedUser.region_code);
                        return region ? (
                          <>
                            <span style={{ fontWeight: 500 }}>{region.regionName || region.region_name}</span>
                            {region.regionNameEn && (
                              <span style={{ color: '#999', fontSize: '12px' }}>({region.regionNameEn})</span>
                            )}
                          </>
                        ) : null;
                      })()}
                    </Space>
                    {(() => {
                      const region = regions.find(r => r.regionCode === selectedUser.region_code || r.region_code === selectedUser.region_code);
                      return region && (region.currencySymbol || region.phonePrefix) ? (
                        <Space size={4}>
                          {region.currencySymbol && (
                            <Tag color="green" style={{ fontSize: '11px' }}>
                              货币: {region.currencySymbol} {region.currencyName || ''}
                            </Tag>
                          )}
                          {region.phonePrefix && (
                            <Tag color="blue" style={{ fontSize: '11px' }}>
                              电话: +{region.phonePrefix}
                            </Tag>
                          )}
                        </Space>
                      ) : null;
                    })()}
                  </Space>
                ) : '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 账户状态 */}
            <Descriptions
              title="账户状态"
              bordered
              column={1}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="账户状态">
                <Tag color={getStatusColor(selectedUser.status)}>
                  {getStatusText(selectedUser.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="实名认证">
                <Tag color={selectedUser.is_verified ? 'success' : 'default'}>
                  {selectedUser.is_verified ? '已认证' : '未认证'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="支付密码">
                <Tag color={selectedUser.has_pay_password ? 'success' : 'warning'}>
                  {selectedUser.has_pay_password ? '已设置' : '未设置'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* 时间信息 */}
            <Descriptions
              title="时间信息"
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="注册时间">
                <Space>
                  <CalendarOutlined />
                  {formatDateTime(selectedUser.created_at)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="最后登录">
                <Space>
                  <CalendarOutlined />
                  {selectedUser.last_login_time ? formatDateTime(selectedUser.last_login_time) : '-'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="最后登录IP">
                {selectedUser.last_login_ip || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {selectedUser.updated_at ? formatDateTime(selectedUser.updated_at) : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      {/* 重置密码模态框 */}
      <Modal
        className="tp-modal"
        title="重置密码"
        open={resetPasswordVisible}
        onCancel={() => setResetPasswordVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmResetPassword}
        >
          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认重置
              </Button>
              <Button onClick={() => setResetPasswordVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置Google验证器确认框 */}
      <Modal
        className="tp-modal"
        title="重置Google验证器"
        open={resetGaVisible}
        onCancel={() => {
          setResetGaVisible(false);
          setSelectedUser(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setResetGaVisible(false);
            setSelectedUser(null);
          }}>
            取消
          </Button>,
          <Button key="confirm" type="primary" danger onClick={handleConfirmResetGa}>
            确认重置
          </Button>
        ]}
      >
        <p style={{ marginBottom: 8 }}>
          确定要重置用户 <strong>{selectedUser?.phone || selectedUser?.email || selectedUser?.real_name || selectedUser?.id}</strong> 的 Google 验证器吗？
        </p>
        <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
          重置后该用户需重新绑定验证器才能使用两步登录。
        </p>
      </Modal>

      {/* 创建用户模态框 */}
      <Modal
        className="tp-modal"
        title="创建用户"
        open={createUserVisible}
        onCancel={() => setCreateUserVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={createUserForm}
          layout="vertical"
          onFinish={handleConfirmCreateUser}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="手机号"
                name="phone"
                rules={[
                  { required: true, message: '请输入手机号' }
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="真实姓名"
                name="realName"
                rules={[
                  { required: true, message: '请输入真实姓名' },
                  { min: 2, max: 20, message: '姓名长度为2-20个字符' }
                ]}
              >
                <Input placeholder="请输入真实姓名" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="登录密码"
                name="password"
                rules={[
                  { required: true, message: '请输入登录密码' },
                  { min: 6, message: '密码至少6位' }
                ]}
              >
                <Input.Password placeholder="请输入登录密码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="确认密码"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="请再次输入密码" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="身份证号"
                name="idCard"
              >
                <Input placeholder="请输入身份证号（可选）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="邮箱"
                name="email"
                rules={[
                  { type: 'email', message: '邮箱格式不正确' }
                ]}
              >
                <Input placeholder="请输入邮箱（可选）" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="所属地区"
                name="regionCode"
              >
                <Select
                  placeholder="请选择地区（可选）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const label = option.label || '';
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {regions.map(region => (
                    <Option 
                      key={region.regionCode} 
                      value={region.regionCode}
                      label={`${region.regionName} ${region.regionNameEn || ''} ${region.regionCode}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <GlobalOutlined style={{ color: '#1890ff' }} />
                          <span style={{ fontWeight: 500 }}>{region.regionName}</span>
                          {region.regionNameEn && (
                            <span style={{ color: '#999', fontSize: '12px' }}>({region.regionNameEn})</span>
                          )}
                        </Space>
                        <Space size={4}>
                          <Tag color="blue" style={{ margin: 0 }}>{region.regionCode}</Tag>
                          {region.currencySymbol && (
                            <Tag color="green" style={{ margin: 0 }}>{region.currencySymbol}</Tag>
                          )}
                        </Space>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}></Col>
          </Row>

          <Row gutter={16}>
            {/* <Col span={12} >
              <Form.Item
                label="用户等级"
                name="userLevel"
                initialValue={1}
              >
                <Select placeholder="请选择用户等级">
                  {userLevels
                    .filter(level => level.status === 1)
                    .map(level => (
                      <Option key={level.id} value={level.level}>
                        Lv.{level.level} - {level.levelName}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col> */}
            <Col span={12}>
              <Form.Item
                label="初始余额"
                name="initialBalance"
                initialValue={0}
                rules={[
                  { type: 'number', min: 0, message: '初始余额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="请输入初始余额"
                  style={{ width: '100%' }}
                  formatter={value => ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/¥\s?|(,*)/g, '')}
                  precision={6}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="邀请码"
            name="invitedByCode"
            help="可选，如果提供邀请码，将建立代理关系"
          >
            <Input placeholder="请输入邀请码（可选）" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={isLoading}>
                创建用户
              </Button>
              <Button onClick={() => setCreateUserVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量删除确认对话框 */}
      <Modal
        className="tp-modal"
        title="批量删除用户"
        open={batchDeleteVisible}
        onOk={handleConfirmBatchDelete}
        onCancel={() => setBatchDeleteVisible(false)}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={isLoading}
      >
        <p>确定要删除选中的 <strong>{selectedRowKeys.length}</strong> 个用户吗？</p>
        <p style={{ color: '#ff4d4f', fontSize: '14px' }}>
          ⚠️ 删除后用户将被禁用，此操作不可撤销！
        </p>
        <p style={{ color: '#666', fontSize: '12px' }}>
          注意：不能删除自己、管理员、有下级用户的用户或有未完成订单的用户
        </p>
      </Modal>

      {/* 单个删除确认对话框 */}
      <Modal
        className="tp-modal"
        title="删除用户"
        open={deleteConfirmVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={isLoading}
      >
        {userToDelete && (
          <div>
            <p>确定要删除用户 <strong>{userToDelete.real_name}</strong> (ID: {userToDelete.id}) 吗？</p>
            <p style={{ color: '#ff4d4f', fontSize: '14px' }}>
              ⚠️ 删除后用户将被禁用，此操作不可撤销！
            </p>
            <p style={{ color: '#666', fontSize: '12px' }}>
              注意：不能删除自己、管理员、有下级用户的用户或有未完成订单的用户
            </p>
          </div>
        )}
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        className="tp-modal"
        title="编辑用户"
        open={editUserVisible}
        onCancel={() => setEditUserVisible(false)}
        footer={null}
        width={600}
      >
        {userToEdit && (
          <div>
            {/* 只读信息展示 */}
            <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#666' }}>基本信息（只读）</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>用户ID：</span>
                    <span>{userToEdit.id}</span>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>手机号：</span>
                    <span>{userToEdit.phone}</span>
                  </div>
                </Col>
                <Col span={12}>
                  {/* <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>邀请码：</span>
                    <span>{userToEdit.invite_code}</span>
                  </div> */}
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>注册时间：</span>
                    <span>{formatDateTime(userToEdit.created_at)}</span>
                  </div>
                </Col>
              </Row>
            </div>

            {/* 可编辑表单 */}
            <Form
              form={editUserForm}
              layout="vertical"
              onFinish={handleConfirmEditUser}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="真实姓名"
                    name="realName"
                    rules={[
                      { required: true, message: '请输入真实姓名' },
                      { min: 2, max: 20, message: '姓名长度为2-20个字符' }
                    ]}
                  >
                    <Input placeholder="请输入真实姓名" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="身份证号"
                    name="idCard"
                  >
                    <Input placeholder="请输入身份证号（可选）" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="邮箱"
                    name="email"
                  >
                    <Input placeholder="请输入邮箱（可选）" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="所属地区"
                    name="regionCode"
                  >
                    <Select
                      placeholder="请选择地区（可选）"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) => {
                        const label = option.label || '';
                        return label.toLowerCase().includes(input.toLowerCase());
                      }}
                    >
                      {regions.map(region => (
                        <Option 
                          key={region.regionCode} 
                          value={region.regionCode}
                          label={`${region.regionName} ${region.regionNameEn || ''} ${region.regionCode}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Space>
                              <GlobalOutlined style={{ color: '#1890ff' }} />
                              <span style={{ fontWeight: 500 }}>{region.regionName}</span>
                              {region.regionNameEn && (
                                <span style={{ color: '#999', fontSize: '12px' }}>({region.regionNameEn})</span>
                              )}
                            </Space>
                            <Space size={4}>
                              <Tag color="blue" style={{ margin: 0 }}>{region.regionCode}</Tag>
                              {region.currencySymbol && (
                                <Tag color="green" style={{ margin: 0 }}>{region.currencySymbol}</Tag>
                              )}
                            </Space>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                {/* <Col span={12}>
                  <Form.Item
                    label="用户等级"
                    name="userLevel"
                    rules={[
                      { required: true, message: '请选择用户等级' }
                    ]}
                  >
                    <Select placeholder="请选择用户等级">
                      {userLevels
                        .filter(level => level.status === 1)
                        .map(level => (
                          <Option key={level.id} value={level.level}>
                            Lv.{level.level} - {level.levelName}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col> */}
                <Col span={12}>
                  <Form.Item
                    label="账户余额"
                    name="balance"
                    rules={[
                      { required: true, message: '请输入账户余额' },
                      { type: 'number', message: '请输入有效的数字' }
                    ]}
                    help="支持负数余额（如用户欠款）"
                  >
                    <InputNumber
                      placeholder="请输入账户余额"
                      style={{ width: '100%' }}
                      formatter={value => ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/¥\s?|(,*)/g, '')}
                      precision={6}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="账户状态"
                    name="status"
                    rules={[
                      { required: true, message: '请选择账户状态' }
                    ]}
                  >
                    <Select>
                      <Option value={0}>禁用</Option>
                      <Option value={1}>正常</Option>
                      <Option value={2}>冻结</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}></Col>
              </Row>

              <Form.Item
                label="交易密码"
                name="payPassword"
                help="留空则不修改交易密码，如需修改请输入新密码（至少6位）"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || value.trim() === '') {
                        return Promise.resolve(); // 允许为空
                      }
                      if (value.length < 6) {
                        return Promise.reject(new Error('交易密码至少6位'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.Password 
                  placeholder="请输入新交易密码（可选）" 
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isLoading}>
                    保存修改
                  </Button>
                  <Button onClick={() => setEditUserVisible(false)}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 余额调整确认模态框 */}
      <Modal
        className="tp-modal"
        title="确认余额调整"
        open={balanceAdjustConfirmVisible}
        onCancel={() => {
          setBalanceAdjustConfirmVisible(false);
          balanceAdjustForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        {userToEdit && (
          <div>
            <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>用户：</span>
                    <span>{userToEdit.real_name || userToEdit.phone}</span>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>原余额：</span>
                    <span style={{ fontWeight: 'bold' }}>{formatAmount(originalBalance)}</span>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>新余额：</span>
                    <span style={{ fontWeight: 'bold', color: newBalance > originalBalance ? '#52c41a' : '#ff4d4f' }}>
                      {formatAmount(newBalance)}
                    </span>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666' }}>调整金额：</span>
                    <span style={{ fontWeight: 'bold', color: newBalance > originalBalance ? '#52c41a' : '#ff4d4f' }}>
                      {newBalance > originalBalance ? '+' : ''}{formatAmount(newBalance - originalBalance)}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            <Form
              form={balanceAdjustForm}
              layout="vertical"
              onFinish={handleConfirmBalanceAdjust}
              initialValues={{ payPassword: '' }}
            >
              <Form.Item
                label="调整原因"
                name="reason"
                rules={[
                  { required: true, message: '请输入调整原因' },
                  { min: 5, message: '调整原因至少5个字符' }
                ]}
              >
                <Input.TextArea
                  placeholder="请输入余额调整原因（至少5个字符）"
                  rows={4}
                />
              </Form.Item>

              <div style={{ marginBottom: 24, padding: 12, backgroundColor: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                <span style={{ color: '#1890ff' }}>💡 后台管理员调整余额无需输入支付密码</span>
              </div>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={isLoading} danger>
                    确认调整
                  </Button>
                  <Button onClick={() => {
                    setBalanceAdjustConfirmVisible(false);
                    balanceAdjustForm.resetFields();
                  }}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      <Modal
        className="tp-modal"
        title={`商户充值 - ${selectedUser?.phone}`}
        open={rechargeVisible}
        onCancel={() => {
          setRechargeVisible(false);
          setSelectedUser(null);
          amountForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={amountForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await dispatch(merchantRecharge({
                userId: selectedUser.id,
                amount: values.amount,
                remark: values.remark
              })).unwrap();
              message.success('充值成功');
              setRechargeVisible(false);
              dispatch(fetchUsers({ ...filters, ...pagination }));
            } catch (error) {
              message.error(error || '充值失败');
            }
          }}
        >
          <Form.Item
            label="充值金额"
            name="amount"
            rules={[{ required: true, message: '请输入充值金额' }]}
          >
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入充值金额" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={2} placeholder="可选备注" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">确认充值</Button>
              <Button onClick={() => setRechargeVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="tp-modal"
        title={`商户提现 - ${selectedUser?.phone}`}
        open={withdrawVisible}
        onCancel={() => {
          setWithdrawVisible(false);
          setSelectedUser(null);
          amountForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={amountForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await dispatch(merchantWithdraw({
                userId: selectedUser.id,
                amount: values.amount,
                remark: values.remark
              })).unwrap();
              message.success('提现成功');
              setWithdrawVisible(false);
              dispatch(fetchUsers({ ...filters, ...pagination }));
            } catch (error) {
              message.error(error || '提现失败');
            }
          }}
        >
          <Form.Item
            label="提现金额"
            name="amount"
            rules={[{ required: true, message: '请输入提现金额' }]}
          >
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入提现金额" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={2} placeholder="可选备注" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" danger htmlType="submit">确认提现</Button>
              <Button onClick={() => setWithdrawVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserList;
